import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { roundsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ROUTES } from '../../utils/constants';

function StatusBadge({ status }) {
  if (status === 'in_progress' || status === 'active')
    return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full animate-pulse">LIVE</span>;
  if (status === 'completed')
    return <span className="px-2 py-0.5 bg-ch-secondary/10 text-ch-secondary/60 text-xs font-semibold rounded-full">Completed</span>;
  return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">Not Started</span>;
}

/**
 * Manage Events — the admin round list, reached via the hamburger. Round setup,
 * start, finish, and delete all live here (not on the leaderboard home page),
 * mirroring FriDaveRounds.jsx.
 */
export default function ClubhouseRounds() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await roundsAPI.getAll({ eventType: 'open' });
      setRounds(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load rounds');
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStartRound = async (roundId) => {
    try {
      await roundsAPI.start(roundId);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start round');
    }
  };

  const handleFinishRound = async (roundId) => {
    if (!window.confirm('Finish this round? This finalises all scores.')) return;
    try {
      await roundsAPI.complete(roundId);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to finish round');
    }
  };

  const handleDeleteRound = async (roundId, isLive) => {
    const warning = isLive
      ? 'This round is currently live. All scores entered so far will be permanently lost. Delete anyway?'
      : 'Delete this round? This cannot be undone.';
    if (!window.confirm(warning)) return;
    try {
      await roundsAPI.delete(roundId);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete round');
    }
  };

  // Group by event, ordered by round number
  const events = rounds.reduce((acc, round) => {
    const key = round.event_name || 'Event';
    (acc[key] = acc[key] || []).push(round);
    return acc;
  }, {});
  Object.values(events).forEach(list =>
    list.sort((a, b) => (a.round_number ?? 0) - (b.round_number ?? 0))
  );

  return (
    <div className="px-4 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-ch-primary">Manage Events</h1>
        {isAuthenticated && (
          <button
            onClick={() => navigate(ROUTES.CREATE_EVENT)}
            className="px-3 py-2 bg-ch-primary text-white text-sm font-bold rounded-lg hover:bg-ch-secondary transition-colors"
          >
            + New
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <div className="text-center py-8 text-error">{error}</div>
      ) : rounds.length === 0 ? (
        <div className="bg-ch-surface rounded-2xl p-8 text-center border border-ch-secondary/15">
          <p className="text-ch-secondary font-semibold">No guest events yet</p>
          {isAuthenticated && (
            <button
              onClick={() => navigate(ROUTES.CREATE_EVENT)}
              className="mt-4 px-6 py-3 bg-ch-primary text-white font-bold rounded-xl"
            >
              Create First Event
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(events).map(([eventName, eventRounds]) => (
            <section key={eventName}>
              <h2 className="text-base font-bold text-ch-primary mb-2">{eventName}</h2>
              <div className="space-y-3">
                {eventRounds.map(round => {
                  const isLive = round.status === 'in_progress' || round.status === 'active';
                  const isNotStarted = round.status === 'not_started';

                  return (
                    <div key={round.id} className="bg-ch-surface rounded-2xl shadow-sm border border-ch-secondary/15 overflow-hidden">
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-ch-primary">Round {round.round_number}</h3>
                            <StatusBadge status={round.status} />
                          </div>
                          <p className="text-xs text-ch-secondary/70">{round.course_name}</p>
                          {round.player_count > 0 && (
                            <p className="text-xs text-ch-secondary/50 mt-0.5">{round.player_count} players</p>
                          )}
                        </div>
                      </div>

                      {isAuthenticated && (
                        <div className="px-4 pb-3 flex gap-2">
                          {isNotStarted && (
                            <button
                              onClick={() => navigate(`/clubhouse/rounds/${round.id}/setup`)}
                              className="flex-1 py-2 bg-ch-bg text-ch-primary text-sm font-semibold rounded-lg hover:bg-ch-secondary/10 transition-colors"
                            >
                              Set Up Players
                            </button>
                          )}
                          {isNotStarted && round.player_count > 0 && (
                            <button
                              onClick={() => handleStartRound(round.id)}
                              className="flex-1 py-2 bg-ch-primary text-white text-sm font-bold rounded-lg hover:bg-ch-secondary transition-colors"
                            >
                              Start Round
                            </button>
                          )}
                          {isLive && (
                            <button
                              onClick={() => handleFinishRound(round.id)}
                              className="flex-1 py-2 bg-ch-primary text-white text-sm font-bold rounded-lg hover:bg-ch-secondary transition-colors"
                            >
                              Finish Round
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteRound(round.id, isLive)}
                            className="py-2 px-3 bg-red-50 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
